import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, AlertTriangle, TrendingUp, BarChart3, Loader2, Send, Clock, DollarSign, Target, Award, Package } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { dashApi } from "../api";
import { useDashAuth } from "../AuthContext";
import { STAGE_LABEL, STAGE_BADGE, formatFormType, formatRelative, formatMoney } from "../utils";
import PipelineBoard from "../components/PipelineBoard";
import AiBeaconWidget from "../components/AiBeaconWidget";

const ACCENT = {
  blue: "bg-blue-50 text-blue-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  violet: "bg-violet-50 text-violet-600",
  rose: "bg-rose-50 text-rose-600",
};

const KpiCard = ({ icon: Icon, label, value, hint, accent = "blue", testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
        <div className="text-3xl font-heading font-bold mt-1 text-slate-900">{value}</div>
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      </div>
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${ACCENT[accent] || ACCENT.blue}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </Card>
);

export default function OverviewPage() {
  const { user } = useDashAuth();
  const [stats, setStats] = useState(null);
  const [failedStats, setFailedStats] = useState(null);
  const [samplesPending, setSamplesPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [digestStatus, setDigestStatus] = useState(null);
  const [digestSending, setDigestSending] = useState(false);

  const loadStats = async () => {
    try {
      const [{ data }, failedRes, samplesRes] = await Promise.all([
        dashApi.get("/stats"),
        dashApi.get("/failed-submissions/stats").catch(() => ({ data: null })),
        dashApi.get("/sample-requests?status=pending&limit=500").catch(() => ({ data: null })),
      ]);
      setStats(data);
      setFailedStats(failedRes.data);
      setSamplesPending(samplesRes?.data?.count ?? null);
    } catch (e) {
      setErr(e.message || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  const loadDigestStatus = async () => {
    try {
      const { data } = await dashApi.get("/digest/status");
      setDigestStatus(data);
    } catch (e) {
      // Non-fatal — digest endpoint is admin-only sugar and may 401 for
      // freshly-logged-in users while the cookie is being verified.
      if (typeof console !== "undefined") console.debug("digest status skipped:", e?.message);
    }
  };

  useEffect(() => {
    loadStats();
    if (user?.role === "admin") loadDigestStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const onSendDigest = async () => {
    if (!window.confirm(
      "Send the stale-leads digest now? This will email every admin a fresh snapshot."
    )) return;
    setDigestSending(true);
    try {
      const { data } = await dashApi.post("/digest/run-now");
      const total = (data.strict_count || 0) + (data.rolling_count || 0);
      const recipients = (data.delivered_to || []).length;
      toast.success(
        total === 0
          ? `Digest sent (no stale leads) to ${recipients} admin${recipients === 1 ? "" : "s"}.`
          : `Digest sent: ${total} stale lead${total === 1 ? "" : "s"} → ${recipients} admin${recipients === 1 ? "" : "s"}.`
      );
      loadDigestStatus();
    } catch (e) {
      toast.error(e.message || "Failed to send digest");
    } finally {
      setDigestSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading dashboard…
      </div>
    );
  }
  if (err) {
    return <div className="text-rose-700 bg-rose-50 border border-rose-200 p-4 rounded-md">{err}</div>;
  }

  const trend = stats.trend_pct;
  const trendLabel = trend === null || trend === undefined
    ? "vs prior 7 days — no baseline yet"
    : `${trend >= 0 ? "+" : ""}${trend}% vs prior 7 days`;

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Overview</h1>
          <p className="text-sm text-slate-500 mt-1">Snapshot of pipeline health and recent activity.</p>
        </div>
        {user?.role === "admin" && (
          <div className="flex flex-col items-end gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onSendDigest}
              disabled={digestSending}
              data-testid="send-digest-btn"
            >
              {digestSending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send digest now
            </Button>
            {digestStatus && digestStatus.last_sent_at && (
              <p className="text-xs text-slate-500" data-testid="digest-last-sent">
                Last digest sent {formatRelative(digestStatus.last_sent_at)}
              </p>
            )}
            {digestStatus && digestStatus.never_run && (
              <p className="text-xs text-slate-500">Digest never run yet</p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          icon={Inbox}
          label="Open leads"
          value={stats.total_open}
          hint="across new → negotiation"
          testid="kpi-open"
        />
        <KpiCard
          icon={TrendingUp}
          label="New last 7 days"
          value={stats.new_last_7d}
          hint={trendLabel}
          accent="emerald"
          testid="kpi-7d"
        />
        <KpiCard
          icon={Clock}
          label="Overdue follow-ups"
          value={stats.overdue_followups ?? 0}
          hint="Past due — needs attention"
          accent="rose"
          testid="kpi-overdue"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Untouched > 24h"
          value={stats.untouched_over_24h}
          hint="New leads with no follow-up"
          accent="amber"
          testid="kpi-stale"
        />
        <KpiCard
          icon={BarChart3}
          label="Stages active"
          value={Object.keys(stats.by_status || {}).length}
          hint="distinct pipeline stages"
          accent="violet"
          testid="kpi-stages"
        />
        {samplesPending !== null && (
          <KpiCard
            icon={Package}
            label="Sample requests"
            value={samplesPending}
            hint="Pending fulfillment"
            accent="rose"
            testid="kpi-samples-pending"
          />
        )}
      </div>

      {/* Revenue tracking */}
      {stats.revenue && (
        <Card className="p-5" data-testid="revenue-tracking-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h2 className="font-heading font-semibold text-slate-900 inline-flex items-center gap-2">
                <DollarSign className="h-4 w-4" /> Revenue tracking
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Closed deals only. Won uses Final Won Value; Lost uses Estimated.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3" data-testid="rev-won-revenue">
              <div className="text-xs uppercase tracking-wider text-emerald-700">Won revenue</div>
              <div className="text-2xl font-heading font-bold text-emerald-900 mt-1">
                {formatMoney(stats.revenue.won_revenue || 0)}
              </div>
              <div className="text-[11px] text-emerald-700 mt-1">
                {stats.revenue.won_count} deal{stats.revenue.won_count === 1 ? "" : "s"}
              </div>
            </div>
            <div className="rounded-md border border-rose-200 bg-rose-50/50 p-3" data-testid="rev-lost-revenue">
              <div className="text-xs uppercase tracking-wider text-rose-700">Lost revenue</div>
              <div className="text-2xl font-heading font-bold text-rose-900 mt-1">
                {formatMoney(stats.revenue.lost_revenue || 0)}
              </div>
              <div className="text-[11px] text-rose-700 mt-1">
                {stats.revenue.lost_count} deal{stats.revenue.lost_count === 1 ? "" : "s"}
              </div>
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3" data-testid="rev-conversion">
              <div className="text-xs uppercase tracking-wider text-blue-700 inline-flex items-center gap-1">
                <Target className="h-3 w-3" /> Conversion
              </div>
              <div className="text-2xl font-heading font-bold text-blue-900 mt-1">
                {stats.revenue.conversion_rate_pct === null
                  ? "—"
                  : `${stats.revenue.conversion_rate_pct}%`}
              </div>
              <div className="text-[11px] text-blue-700 mt-1">won / (won + lost)</div>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3" data-testid="rev-avg-deal">
              <div className="text-xs uppercase tracking-wider text-amber-700 inline-flex items-center gap-1">
                <Award className="h-3 w-3" /> Avg deal size
              </div>
              <div className="text-2xl font-heading font-bold text-amber-900 mt-1">
                {formatMoney(stats.revenue.avg_deal_size || 0)}
              </div>
              <div className="text-[11px] text-amber-700 mt-1">across won deals</div>
            </div>
          </div>
        </Card>
      )}

      {failedStats && (failedStats.unresolved_total > 0 || failedStats.unresolved_last_7d > 0) && (
        <Card
          className="p-5 border-amber-200 bg-amber-50/40"
          data-testid="failed-submissions-tile"
        >
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="font-heading font-semibold text-slate-900">Failed submissions</h2>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Lead attempts that didn't reach your inbox — reach out manually and replay the session in Clarity.
                  </p>
                </div>
                <Link
                  to="/dashboard/failed-submissions"
                  className="text-sm font-medium text-amber-700 hover:text-amber-900 hover:underline"
                  data-testid="failed-tile-cta"
                >
                  Open list →
                </Link>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Unresolved</div>
                  <div className="text-2xl font-heading font-bold text-amber-900" data-testid="failed-stat-total">
                    {failedStats.unresolved_total}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Last 24h</div>
                  <div className="text-2xl font-heading font-bold text-amber-900" data-testid="failed-stat-24h">
                    {failedStats.unresolved_last_24h}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Last 7 days</div>
                  <div className="text-2xl font-heading font-bold text-amber-900" data-testid="failed-stat-7d">
                    {failedStats.unresolved_last_7d}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h2 className="font-heading font-semibold text-slate-900 mb-3">Pipeline by stage</h2>
          {Object.keys(stats.by_status || {}).length === 0 ? (
            <p className="text-sm text-slate-500">No submissions yet.</p>
          ) : (
            <ul className="space-y-2">
              {Object.entries(stats.by_status).map(([stage, count]) => (
                <li key={stage} className="flex items-center justify-between" data-testid={`stage-${stage}-row`}>
                  <span className={`inline-flex items-center text-xs px-2.5 py-1 rounded-full border ${STAGE_BADGE[stage] || "bg-slate-100"}`}>
                    {STAGE_LABEL[stage] || stage}
                  </span>
                  <Link
                    to={`/dashboard/submissions?status=${stage}`}
                    className="text-sm text-slate-900 font-medium hover:underline"
                  >
                    {count}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="font-heading font-semibold text-slate-900 mb-3">Top form types (30d)</h2>
          {(!stats.by_form_type || stats.by_form_type.length === 0) ? (
            <p className="text-sm text-slate-500">No submissions in the last 30 days.</p>
          ) : (
            <ul className="space-y-2">
              {stats.by_form_type.slice(0, 8).map((row) => (
                <li key={row.type} className="flex items-center justify-between" data-testid={`form-type-${row.type}-row`}>
                  <span className="text-sm text-slate-700">{formatFormType(row.type)}</span>
                  <Link
                    to={`/dashboard/submissions?form_type=${row.type}`}
                    className="text-sm text-slate-900 font-medium hover:underline"
                  >
                    {row.count}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="p-5 bg-slate-50 border-dashed">
        <p className="text-sm text-slate-600">
          Need a deeper view?{" "}
          <Link to="/dashboard/submissions" className="text-blue-700 font-medium hover:underline" data-testid="overview-cta-submissions">
            Browse all submissions →
          </Link>
        </p>
      </Card>

      <AiBeaconWidget />

      {/* Embedded pipeline Kanban (compact) */}
      <Card className="p-5" data-testid="overview-pipeline-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-heading font-semibold text-slate-900">Pipeline</h2>
          <Link
            to="/dashboard/pipeline"
            className="text-sm text-blue-700 font-medium hover:underline"
            data-testid="overview-pipeline-link"
          >
            Open full board →
          </Link>
        </div>
        <PipelineBoard compact />
      </Card>
    </div>
  );
}
